import React, {useState} from 'react';
import {Box, Checkbox, FormControlLabel, TextField, Typography, useMediaQuery} from '@mui/material';
import {useTranslation} from "react-i18next";
import {PrimaryBigButton} from '../../components/buttons/mainButton';
import {postEmailContact} from '../../services/EmailContactService';
import {useSearchParams} from 'react-router-dom';
import {PUBLIC_URL} from '../../common/struct/urlManager';

function RgpdConsent(props: { checked: boolean, error?: string, compact?: boolean, onChange: (checked: boolean) => void, sx?: object }): JSX.Element {
  const {checked, error, compact = false, onChange, sx = {}} = props;

  const {t} = useTranslation();

  return (
    <Box sx={{width: "100%", ...sx}}>
      <FormControlLabel
        sx={{alignItems: "flex-start", m: 0, gap: 1}}
        control={
          <Checkbox
            checked={checked}
            onChange={(e): void => onChange(e.target.checked)}
            size={compact ? "small" : "medium"}
            sx={{p: 0, mt: "1px", color: error ? "error.main" : "ornament.main", "&.Mui-checked": {color: "#50DBB9"}}}
          />
        }
        label={
          <Typography
            variant="caption"
            component="span"
            sx={{
              display: "block",
              color: "ornament.main",
              lineHeight: 1.5,
              fontSize: compact ? "11px" : "13px",
              "& a": {color: "#50DBB9", textDecoration: "underline"}
            }}
            dangerouslySetInnerHTML={{__html: t("landing_page.form.rgpd.label", {link: PUBLIC_URL.PPDP})}}
          />
        }
      />
      {error && <Typography variant="body1" sx={{color: "error.main", mt: 1}}>{error}</Typography>}
    </Box>
  );
}

export function DesktopForm(props: { smallConfig?: boolean, isStuck: boolean }): JSX.Element {
  const {smallConfig = true, isStuck} = props;

  const {t} = useTranslation();

  const [searchParams] = useSearchParams();
  const paramsObject = Array.from(searchParams.entries());

  const paramsArray = paramsObject.map(([key, value]) => ({
    name: key,
    value: value,
  }));

  const [errors, setErrors] = useState<any>({});
  const [emailSend, setEmailSend] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [rgpdAccepted, setRgpdAccepted] = useState<boolean>(false);
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    urlParams: JSON.stringify(paramsArray)
  });

  const handleChange = (e: any): void => {
    const {name, value} = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    setErrors((prevErrors: any) => ({
      ...prevErrors,
      [name]: undefined,
    }));
  };

  const handleRgpdChange = (checked: boolean): void => {
    setRgpdAccepted(checked);
    setErrors((prevErrors: any) => ({
      ...prevErrors,
      rgpd: undefined,
    }));
  };

  const sendEmailContact = async (): Promise<void> => {
    if (!rgpdAccepted) {
      setErrors((prevErrors: any) => ({
        ...prevErrors,
        rgpd: t("landing_page.form.rgpd.error"),
      }));
      return;
    }

    setLoading(true);
    postEmailContact(formData)
      .then((res: any) => {
        if (!res) {
          setEmailSend(true);
          setLoading(false);
        } else if (res.errors) {
          setErrors(res.errors);
          setLoading(false);
        }
      });
  }

  const breakpoint = useMediaQuery('(min-width: 1100px)');

  return (
    <>
      {smallConfig ? <>
        <Typography variant="h1" sx={{color: "#50DBB9", width: "fit-content", fontWeight: "medium", mb: !isStuck ? 4 : "0px", opacity: !isStuck ? 1 : 0, maxHeight: !isStuck ? "300px" : "0px", overflow: "hidden", transition: "max-height 0.1ms ease, opacity 0.1ms ease"}} dangerouslySetInnerHTML={{__html: t("landing_page.form.title")}} />
        <Box sx={{display: "flex", alignItems: "stretch", gap: "20px", width: "100%", maxWidth: "1440px", margin: "0 auto", flexDirection: breakpoint ? "row" : isStuck ? "row" : "column"}}>
          <Box sx={{display: "flex", flexDirection: "column", justifyContent: "space-between", width: breakpoint ? (isStuck ? "100%" : "50%") : "100%", gap: !isStuck ? "20px" : "4px"}}>
            <Box sx={{display: "flex", width: "100%", gap: "20px"}}>
              <Box sx={{width: "100%", "input::placeholder": {color: isStuck ? "black" : "unset", fontWeight: isStuck ? 600 : "unset"}}}>
                <Typography variant="body1" sx={{color: "ornament.main", mb: !isStuck ? 1 : "0px", opacity: !isStuck ? 1 : 0, maxHeight: !isStuck ? "300px" : "0px", overflow: "hidden", transition: "max-height 0.1ms ease, opacity 0.1ms ease"}}>{t("landing_page.form.field.email")}</Typography>
                <TextField placeholder={!isStuck ? t("landing_page.form.field.email_placeholder") : t("landing_page.form.field.email_placeholder_small")} name="email" value={formData.email} onChange={handleChange} sx={{backgroundColor: "ornament.main", borderWidth: "2px", borderRadius: "3px", width: "100%", borderColor: errors.email ? "error.main" : "#15154B", borderStyle: "solid", "& fieldset": {border: 'none'}}} size="small"></TextField>
                {errors.email && <Typography variant="body1" sx={{color: "error.main", mt: !isStuck ? 1 : 0}}>{errors.email}</Typography>}
              </Box>
              <Box sx={{width: "100%", "input::placeholder": {color: isStuck ? "black" : "unset", fontWeight: isStuck ? 600 : "unset"}}}>
                <Typography variant="body1" sx={{color: "ornament.main", mb: !isStuck ? 1 : "0px", opacity: !isStuck ? 1 : 0, maxHeight: !isStuck ? "300px" : "0px", overflow: "hidden", transition: "max-height 0.1ms ease, opacity 0.1ms ease"}}>{t("landing_page.form.field.phone")}</Typography>
                <TextField placeholder={!isStuck ? t("landing_page.form.field.phone_placeholder") : t("landing_page.form.field.phone_placeholder_small")} name="phone" value={formData.phone} onChange={handleChange} sx={{backgroundColor: "ornament.main", borderWidth: "2px", borderRadius: "3px", width: "100%", borderColor: errors.phone ? "error.main" : "#15154B", borderStyle: "solid", "& fieldset": {border: 'none'}}} size="small"></TextField>
                {errors.phone && <Typography variant="body1" sx={{color: "error.main", mt: !isStuck ? 1 : 0}}>{errors.phone}</Typography>}
              </Box>
            </Box>
            <Box sx={{width: "100%", "input::placeholder": {color: isStuck ? "black" : "unset", fontWeight: isStuck ? 600 : "unset"}}}>
              <Typography variant="body1" sx={{color: "ornament.main", mb: !isStuck ? 1 : "0px", opacity: !isStuck ? 1 : 0, maxHeight: !isStuck ? "300px" : "0px", overflow: "hidden", transition: "max-height 0.1ms ease, opacity 0.1ms ease"}}>{t("landing_page.form.field.company")}</Typography>
              <TextField placeholder={!isStuck ? t("landing_page.form.field.company_placeholder") : t("landing_page.form.field.company_placeholder_small")} name="company" value={formData.company} onChange={handleChange} sx={{backgroundColor: "ornament.main", borderWidth: "2px", borderRadius: "3px", width: "100%", borderColor: errors.company ? "error.main" : "#15154B", borderStyle: "solid", "& fieldset": {border: 'none'}}} size="small"></TextField>
              {errors.company && <Typography variant="body1" sx={{color: "error.main", mt: !isStuck ? 1 : 0}}>{errors.company}</Typography>}
            </Box>
          </Box>
          <Box sx={{display: "flex", flexDirection: "column", width: breakpoint ? "50%" : "100%", "textarea::placeholder": {color: isStuck ? "black" : "unset", textOverflow: "ellipsis", fontWeight: isStuck ? 600 : "unset"}}}>
            <Typography variant="body1" sx={{color: "ornament.main", mb: !isStuck ? 1 : "0px", opacity: !isStuck ? 1 : 0, maxHeight: !isStuck ? "300px" : "0px", minHeight: "24px", overflow: "hidden", transition: "max-height 0.1ms ease, opacity 0.1ms ease"}}>{t("landing_page.form.field.message")}</Typography>
            <TextField placeholder={t("landing_page.form.field.message_placeholder")} name="message" multiline value={formData.message} onChange={handleChange} sx={{backgroundColor: "ornament.main", borderWidth: "2px", borderRadius: "3px", width: "100%", height: breakpoint ? "100%" : "100px", borderColor: errors.message ? "error.main" : "#15154B", borderStyle: "solid", "& fieldset": {border: 'none'}}} size="small"></TextField>
            {errors.message && <Typography variant="body1" sx={{color: "error.main", mt: !isStuck ? 1 : 0}}>{errors.message}</Typography>}
          </Box>
          {isStuck && <>{emailSend ?
            <Box sx={{width: "18%", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between"}}>
              <Box sx={{backgroundColor: "#50DBB9", boxSizing: "border-box", height: "fit-content", whiteSpace: "nowrap", display: "flex", justifyContent: "center", alignItems: "center", borderRadius: "5px", padding: "16px 24px"}}>
                <Typography variant="body1" sx={{color: "#15154B", fontWeight: "bold", textAlign: "center"}}>{t("landing_page.form.send_button")}</Typography>
              </Box>
            </Box> :
            <Box sx={{width: "fit-content", display: "flex", alignItems: "center"}}>
              <PrimaryBigButton action={sendEmailContact} label={t("landing_page.form.button")} sx={{textTransform: "uppercase", textAlign: "center", fontWeight: 700, backgroundColor: "#50DBB9", whiteSpace: "nowrap", width: "fit-content", color: "#15154B", height: "42px", "&:hover": {backgroundColor: "ornament.main", color: "#15154B"}, "& .Mui-disabled": {backgroundColor: "#50DBB9", color: "#15154B"}}} loading={loading} />
            </Box>
          }</>}
        </Box>
        {!emailSend && <RgpdConsent checked={rgpdAccepted} error={errors.rgpd} compact={isStuck} onChange={handleRgpdChange} sx={{mt: isStuck ? 1 : 3}} />}
        {!isStuck && <>{emailSend ?
          <Box sx={{width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", mt: 4}}>
            <Typography variant="body1" sx={{color: "ornament.main", mr: 3, textAlign: "center"}}>{t("landing_page.form.send_message")}</Typography>
            <Box sx={{backgroundColor: "#50DBB9", boxSizing: "border-box", height: "fit-content", display: "flex", justifyContent: "center", alignItems: "center", borderRadius: "5px", padding: "16px 24px"}}>
              <Typography variant="body1" sx={{color: "#15154B", fontWeight: "bold", textAlign: "center"}}>{t("landing_page.form.send_button")}</Typography>
            </Box>
          </Box> :
          <Box sx={{width: "100%", display: "flex", justifyContent: "end", mt: 4}}>
            <PrimaryBigButton action={sendEmailContact} label={t("landing_page.form.button")} sx={{textTransform: "uppercase", textAlign: "center", fontWeight: 700, backgroundColor: "#50DBB9", color: "#15154B", height: "42px", "&:hover": {backgroundColor: "ornament.main", color: "#15154B"}, "& .Mui-disabled": {backgroundColor: "#50DBB9", color: "#15154B"}}} loading={loading} />
          </Box>
        }</>}
      </> :
        <>
          <Box sx={{display: "flex", alignItems: "start", gap: "20px", boxSizing: "border-box", width: "100%", flexDirection: "column"}}>
            <Box sx={{width: "100%", display: "flex", gap: "20px"}}>
              <Box sx={{width: "50%"}}>
                <Typography variant="body1" sx={{color: "ornament.main", mb: 1}}>{t("landing_page.form.field.firstname")}</Typography>
                <TextField placeholder={t("landing_page.form.field.firstname_placeholder")} name="firstname" value={formData.firstname} onChange={handleChange} sx={{backgroundColor: "ornament.main", borderRadius: "3px", width: "100%", borderColor: "#15154B", borderStyle: "solid", borderWidth: "2px", "& fieldset": {border: 'none'}}} size="small"></TextField>
              </Box>
              <Box sx={{width: "50%"}}>
                <Typography variant="body1" sx={{color: "ornament.main", mb: 1}}>{t("landing_page.form.field.lastname")}</Typography>
                <TextField placeholder={t("landing_page.form.field.lastname_placeholder")} name="lastname" value={formData.lastname} onChange={handleChange} sx={{backgroundColor: "ornament.main", borderRadius: "3px", width: "100%", borderColor: "#15154B", borderStyle: "solid", borderWidth: "2px", "& fieldset": {border: 'none'}}} size="small"></TextField>
              </Box>
            </Box>
            <Box sx={{width: "100%"}}>
              <Typography variant="body1" sx={{color: "ornament.main", mb: 1}}>{t("landing_page.form.field.company")}</Typography>
              <TextField placeholder={t("landing_page.form.field.company_placeholder")} name="company" value={formData.company} onChange={handleChange} sx={{backgroundColor: "ornament.main", borderRadius: "3px", width: "100%", borderColor: errors.company ? "error.main" : "#15154B", borderStyle: "solid", borderWidth: "2px", "& fieldset": {border: 'none'}}} size="small"></TextField>
              {errors.company && <Typography variant="body1" sx={{color: "error.main", mt: 1}}>{errors.company}</Typography>}
            </Box>
            <Box sx={{width: "100%"}}>
              <Typography variant="body1" sx={{color: "ornament.main", mb: 1}}>{t("landing_page.form.field.email")}</Typography>
              <TextField placeholder={t("landing_page.form.field.email_placeholder")} name="email" value={formData.email} onChange={handleChange} sx={{backgroundColor: "ornament.main", borderRadius: "3px", width: "100%", borderColor: errors.email ? "error.main" : "#15154B", borderStyle: "solid", borderWidth: "2px", "& fieldset": {border: 'none'}}} size="small"></TextField>
              {errors.email && <Typography variant="body1" sx={{color: "error.main", mt: 1}}>{errors.email}</Typography>}
            </Box>
            <Box sx={{width: "100%"}}>
              <Typography variant="body1" sx={{color: "ornament.main", mb: 1}}>{t("landing_page.form.field.phone")}</Typography>
              <TextField placeholder={t("landing_page.form.field.phone_placeholder")} name="phone" value={formData.phone} onChange={handleChange} sx={{backgroundColor: "ornament.main", borderRadius: "3px", width: "100%", borderColor: errors.phone ? "error.main" : "#15154B", borderStyle: "solid", borderWidth: "2px", "& fieldset": {border: 'none'}}} size="small"></TextField>
              {errors.phone && <Typography variant="body1" sx={{color: "error.main", mt: 1}}>{errors.phone}</Typography>}
            </Box>
            <Box sx={{width: "100%"}}>
              <Typography variant="body1" sx={{color: "ornament.main", mb: 1}}>{t("landing_page.form.field.message")}</Typography>
              <TextField placeholder={t("landing_page.form.field.message_placeholder")} name="message" multiline rows={6} value={formData.message} onChange={handleChange} sx={{backgroundColor: "ornament.main", borderRadius: "3px", width: "100%", height: breakpoint ? "100%" : "100px", borderColor: errors.message ? "error.main" : "#15154B", borderStyle: "solid", borderWidth: "2px", "& fieldset": {border: 'none'}}} size="small"></TextField>
              {errors.message && <Typography variant="body1" sx={{color: "error.main", mt: 1}}>{errors.message}</Typography>}
            </Box>
            {!emailSend && <RgpdConsent checked={rgpdAccepted} error={errors.rgpd} onChange={handleRgpdChange} />}
            <Box sx={{width: "100%", display: "flex", justifyContent: "end", mt: 4}}>
              {emailSend ?
                <Box sx={{width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                  <Typography variant="body1" sx={{color: "ornament.main"}}>{t("landing_page.form.send_message")}</Typography>
                  <Box sx={{backgroundColor: "#50DBB9", boxSizing: "border-box", height: "fit-content", display: "flex", justifyContent: "center", alignItems: "center", borderRadius: "5px", padding: "16px 24px"}}>
                    <Typography variant="body1" sx={{textTransform: "uppercase", textAlign: "center", color: "#15154B", fontWeight: "bold"}}>{t("landing_page.form.send_button")}</Typography>
                  </Box>
                </Box> :
                <PrimaryBigButton action={sendEmailContact} label={t("landing_page.form.send")} sx={{textTransform: "uppercase", fontWeight: 700, backgroundColor: "#50DBB9", color: "#15154B", height: "42px", "&:hover": {backgroundColor: "ornament.main", color: "#15154B"}, "& .Mui-disabled": {backgroundColor: "#50DBB9", color: "#15154B"}}} loading={loading} />
              }
            </Box>
          </Box>
        </>
      }
    </>
  )
}